<template>
  <v-dialog :value="value" width="720px" persistent @input="updateDialog">
    <v-card>
      <v-card-title class="subtitle-1">Credit Card Fee</v-card-title>
      <v-card-text style="max-height: 60vh; overflow-y: auto">
        <div class="caption grey--text mb-3">
          Supplier default for new products, one row per year. The highlighted year is the one shown on the product list.
          Save Default stores these without changing existing products. Apply To All also writes filled years onto existing
          products. Clear a year and save to stop new products from getting that fee.
        </div>
        <div v-for="year in feeYears" :key="year" :class="{ 'focus-year': isFocusYear(year) }">
          <ExtraDialogRow
            :config="yearConfig(year)"
            :data="feesByYear[year]"
            :country="country"
            @update="(payload) => updateYearFee(year, payload)"
            @remove="clearYearFee(year)"
          />
        </div>
      </v-card-text>
      <v-card-actions>
        <div class="flex-grow-1"></div>
        <g-button label="Cancel" icon="clear" @onclick="closeDialog" />
        <g-button label="Remove From All" icon="delete" @onclick="removeCreditCardFeeFromAllProducts" />
        <g-button label="Save Default" icon="save" @onclick="saveCreditCardFeeDefaults" />
        <g-button type="primary" label="Apply To All" icon="done_all" @onclick="applyCreditCardFeeToAllProducts" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from "vue";
import ExtraDialogRow from "@/modules/products/components/extraDialog/_ExtraDialogRow";
import EXTRA_OPTIONS from "@/config/products/EXTRA_OPTIONS";

const CREDIT_CARD_FEE_KEY = "extra_credit_card_fee";

export default {
  name: "CreditCardFeeDialog",
  components: {
    ExtraDialogRow,
  },
  props: {
    value: Boolean,
    productList: {
      type: Array,
      default: () => [],
    },
    focusYear: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      feesByYear: {},
    };
  },
  computed: {
    country() {
      return this.$store.getters["AccountStore/environment"];
    },
    feeYears() {
      const years = new Set();
      const currentYear = new Date().getFullYear();
      years.add(String(currentYear));
      years.add(String(currentYear + 1));

      (this.productList || []).forEach((product) => {
        (product.rate_history || []).forEach((rate) => {
          if (rate.rate_year) years.add(String(rate.rate_year));
        });
      });
      Object.keys(this.getCreditCardFeeMap()).forEach((year) => years.add(String(year)));

      return Array.from(years).sort((a, b) => Number(b) - Number(a));
    },
  },
  watch: {
    value(open) {
      if (open) this.resetFeesByYear();
    },
  },
  methods: {
    yearConfig(year) {
      return {
        ...EXTRA_OPTIONS[CREDIT_CARD_FEE_KEY],
        label: String(year),
      };
    },
    isFocusYear(year) {
      return String(year) === String(this.focusYear);
    },
    getDefaultCreditCardFeeData() {
      return {
        astype: EXTRA_OPTIONS[CREDIT_CARD_FEE_KEY].defaultType,
        value: null,
      };
    },
    feeHasValue(fee) {
      return fee && fee.value !== null && fee.value !== undefined && fee.value !== "";
    },
    resetFeesByYear() {
      const next = {};
      this.feeYears.forEach((year) => {
        const saved = this.getSavedCreditCardFee(year) || this.getSharedProductFee(year);
        next[year] = saved ? JSON.parse(JSON.stringify(saved)) : this.getDefaultCreditCardFeeData();
      });
      this.feesByYear = next;
    },
    getSavedCreditCardFee(year) {
      return this.getCreditCardFeeMap()[`${year}`] || null;
    },
    getSharedProductFee(year) {
      const fees = [];
      (this.productList || []).forEach((product) => {
        const rate = this.getProductRate(product, year);
        if (!rate) return;
        fees.push((rate.meta && rate.meta[CREDIT_CARD_FEE_KEY]) || null);
      });
      const present = fees.filter(Boolean);
      if (!present.length || present.length !== fees.length) return null;

      const first = JSON.stringify(present[0]);
      if (!present.every((fee) => JSON.stringify(fee) === first)) return null;
      return JSON.parse(first);
    },
    getCreditCardFeeMap() {
      const fees = this.$store.getters["SupplierStore/meta"] && this.$store.getters["SupplierStore/meta"].credit_card_fee;
      if (!fees || typeof fees !== "object" || Array.isArray(fees)) return {};
      return JSON.parse(JSON.stringify(fees));
    },
    buildFeeMapFromEditor() {
      const next = {};
      Object.keys(this.feesByYear).forEach((year) => {
        if (!this.feeHasValue(this.feesByYear[year])) return;
        next[year] = JSON.parse(JSON.stringify(this.feesByYear[year]));
      });
      return next;
    },
    saveSupplierCreditCardFee(feeByYear, cacheFees) {
      const supplierId = this.$store.getters["SupplierStore/id"];
      if (!supplierId) return Promise.reject("Supplier not found");

      const nextFees = feeByYear && Object.keys(feeByYear).length ? feeByYear : null;
      return this.$store
        .dispatch("SupplierStore/updateSupplierMeta", {
          supplierId,
          patch: { credit_card_fee: nextFees },
          cache: { credit_card_fee: cacheFees && Object.keys(cacheFees).length ? cacheFees : null },
        })
        .then(() => {
          this.$store.dispatch("SupplierStore/setUpdateMeta", {
            key: "credit_card_fee",
            value: nextFees,
            remove: !nextFees,
            syncCache: true,
          });
          return cacheFees;
        });
    },
    restoreSupplierCreditCardFee(previousFees) {
      return this.saveSupplierCreditCardFee(previousFees || {}, this.getCreditCardFeeMap());
    },
    rollbackProductRates(updates) {
      updates.forEach(({ product, year, cache }) => {
        const index = product.rate_history.findIndex((rate) => String(rate.rate_year) === String(year));
        if (index >= 0) Vue.set(product.rate_history, index, cache);
      });
    },
    updateDialog(value) {
      this.$emit("input", value);
    },
    closeDialog() {
      this.updateDialog(false);
    },
    updateYearFee(year, { key, value }) {
      if (!this.feesByYear[year]) Vue.set(this.feesByYear, year, this.getDefaultCreditCardFeeData());
      Vue.set(this.feesByYear[year], key, value);
    },
    clearYearFee(year) {
      Vue.set(this.feesByYear, year, this.getDefaultCreditCardFeeData());
    },
    getProductRate(product, year) {
      if (!product.rate_history) return null;
      return product.rate_history.find((rate) => String(rate.rate_year) === String(year));
    },
    saveCreditCardFeeDefaults() {
      const previousFees = this.getCreditCardFeeMap();
      this.$root.$loading.open("Saving credit card fee...");
      return this.saveSupplierCreditCardFee(this.buildFeeMapFromEditor(), previousFees)
        .then(() => {
          this.$root.$loading.end();
          this.closeDialog();
          this.$root.$success("Saved credit card fee defaults");
        })
        .catch((err) => {
          this.$root.$loading.end();
          this.$root.$error(err);
        });
    },
    finishSupplierFeeSave(previousFees, updates, label) {
      const persist = this.saveSupplierCreditCardFee(this.buildFeeMapFromEditor(), previousFees);
      if (!updates.length) {
        return persist
          .then(() => {
            this.$root.$loading.end();
            this.closeDialog();
            this.$root.$success(`${label} credit card fee defaults`);
          })
          .catch((err) => {
            this.$root.$loading.end();
            this.$root.$error(err);
          });
      }

      return persist
        .then(() => this.$store.dispatch("ProductStore/updateProductRatesForProducts", updates))
        .then(() => {
          this.$root.$loading.end();
          this.closeDialog();
          this.$root.$success(`${label} credit card fee for ${updates.length} product rates`);
        })
        .catch((err) => {
          this.rollbackProductRates(updates);
          return this.restoreSupplierCreditCardFee(previousFees)
            .catch(() => {})
            .then(() => {
              this.$root.$loading.end();
              this.$root.$error(err);
            });
        });
    },
    applyCreditCardFeeToAllProducts() {
      const updates = [];
      this.feeYears.forEach((year) => {
        const fee = this.feesByYear[year];
        if (!this.feeHasValue(fee)) return;

        this.productList.forEach((product) => {
          const rate = this.getProductRate(product, year);
          if (!rate) return;

          const cache = JSON.parse(JSON.stringify(rate));
          if (!rate.meta) Vue.set(rate, "meta", {});
          Vue.set(rate.meta, CREDIT_CARD_FEE_KEY, JSON.parse(JSON.stringify(fee)));
          updates.push({
            product,
            productId: product.id,
            year,
            data: rate,
            cache,
          });
        });
      });

      this.$root.$loading.open("Applied credit card fee...");
      return this.finishSupplierFeeSave(this.getCreditCardFeeMap(), updates, "Applied");
    },
    removeCreditCardFeeFromAllProducts() {
      const updates = [];
      this.feeYears.forEach((year) => {
        this.productList.forEach((product) => {
          const rate = this.getProductRate(product, year);
          if (!rate || !rate.meta || !rate.meta[CREDIT_CARD_FEE_KEY]) return;

          const cache = JSON.parse(JSON.stringify(rate));
          Vue.delete(rate.meta, CREDIT_CARD_FEE_KEY);
          updates.push({
            product,
            productId: product.id,
            year,
            data: rate,
            cache,
          });
        });
      });

      if (!updates.length && !Object.keys(this.getCreditCardFeeMap()).length) {
        this.closeDialog();
        return this.$root.$error("No credit card fees found");
      }

      this.feeYears.forEach((year) => this.clearYearFee(year));
      this.$root.$loading.open("Removed credit card fee...");
      return this.finishSupplierFeeSave(this.getCreditCardFeeMap(), updates, "Removed");
    },
  },
};
</script>

<style scoped>
.focus-year {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}
</style>
